import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Card } from '@/shared/ui/Card';
import { HealthIndicator } from '@/shared/ui/Indicator';

import { CAPABILITIES } from '../model/capabilities';
import type { CapabilitySupport } from '../model/capabilities';

/* What this deployment can do with this source family, one line per capability.
 *
 * **The three answers come from `GET /v1/catalog/connectors`, never from a list in the client.** That
 * is not tidiness: this claim was once a hardcoded trio of connector names, and the day Snowflake and
 * Databricks became harvestable every card for one said "has no driver in this build" beside a source
 * this platform can harvest. A frontend copy of a backend capability goes stale in the direction that
 * makes the platform look worse than it is, and nothing fails when it does.
 *
 * **`unknown` draws a hollow dot, and that is the load-bearing detail rather than the colour.**
 * `Indicator` renders absence hollow so "we could not read the catalogue" survives greyscale, a
 * photocopier and every form of colour blindness — the same treatment `StatusBadge` gives it, so the
 * two cannot come to disagree about how absence looks. A capability we could not read must not be
 * mistaken for one that is switched off: the first sends a reader to check the deployment, the second
 * tells them to use a different source.
 */
export interface SourceCapabilitiesProps {
  readonly support: CapabilitySupport;
}

export function SourceCapabilities({ support }: SourceCapabilitiesProps): ReactElement {
  const { t } = useTranslation();

  return (
    <Card title={t('connectionDetail.supports')}>
      <ul className="lq-capabilities">
        {CAPABILITIES.map((capability) => {
          const answer = support[capability];
          return (
            <li key={capability} className="lq-capabilities__row">
              <HealthIndicator
                health={
                  answer === 'yes' ? 'healthy' : answer === 'no' ? 'degraded' : 'unknown'
                }
                /* The label is the capability, and the *explanation* is a separate line below rather
                 * than a title attribute: this card is the answer to "why can I not run rules against
                 * this source", and an answer that only appears on hover is not an answer on a
                 * touch device. */
                label={t(`connectionDetail.capability.${capability}.label`)}
              />
              <p className="lq-capabilities__why">
                {t(`connectionDetail.capability.${capability}.${answer}`)}
              </p>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
